'use client'

import * as React from 'react'
import Link from 'next/link'
import { ArrowRight, Search, X } from 'lucide-react'
import {
  GLOSSARY,
  GLOSSARY_CLUSTERS,
  CLUSTER_ACCENT,
  type GlossaryCluster,
  type GlossaryEntry,
} from '@/lib/copy/glossary'

// Listing UI for /glossary. Two interactive controls:
//   1. Free-text filter, matches against term + aliases + shortAnswer.
//   2. Cluster filter chips, single-select with an "All" reset.
// Falls through to an alphabet-grouped term grid. Each card links to
// the per-term /glossary/[slug] page.

export function GlossaryListing() {
  const [q, setQ] = React.useState('')
  const [active, setActive] = React.useState<GlossaryCluster | 'all'>('all')

  const filtered = React.useMemo(() => {
    const needle = q.trim().toLowerCase()
    return GLOSSARY.filter((e) => {
      if (active !== 'all' && e.cluster !== active) return false
      if (!needle) return true
      const haystack = [
        e.term,
        e.shortAnswer,
        ...(e.aliases || []),
      ].join(' ').toLowerCase()
      return haystack.includes(needle)
    })
  }, [q, active])

  const grouped = React.useMemo(() => {
    const map: Record<string, GlossaryEntry[]> = {}
    for (const e of [...filtered].sort((a, b) => a.term.localeCompare(b.term))) {
      // Strip leading non-letter chars (e.g. '#') and parens.
      const letter = (e.term.replace(/^["'#]+/, '')[0] || '#').toUpperCase()
      if (!map[letter]) map[letter] = []
      map[letter].push(e)
    }
    return map
  }, [filtered])

  const letters = Object.keys(grouped).sort()

  return (
    <>
      <section className="container-tight pb-6">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={q}
            onChange={(ev) => setQ(ev.target.value)}
            placeholder="Search 125 terms, aliases or definitions..."
            className="w-full rounded-full border bg-card py-3 pl-11 pr-12 text-sm font-medium placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
            aria-label="Search glossary"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Cluster filter chips */}
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setActive('all')}
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] transition ${active === 'all' ? 'border-foreground bg-foreground text-background' : 'bg-card text-foreground/75 hover:border-primary/50'}`}
          >
            All ({GLOSSARY.length})
          </button>
          {GLOSSARY_CLUSTERS.map((c) => {
            const count = GLOSSARY.filter((e) => e.cluster === c.id).length
            const accent = CLUSTER_ACCENT[c.id]
            const isActive = active === c.id
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setActive(c.id)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] transition ${isActive ? 'border-foreground bg-foreground text-background' : `bg-card text-foreground/75 hover:border-primary/50`}`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${accent.dot}`} />
                {c.label}
                <span className="ml-1 text-foreground/45">{count}</span>
              </button>
            )
          })}
        </div>

        {q && filtered.length === 0 && (
          <p className="mt-8 text-sm text-muted-foreground">
            No terms match &ldquo;{q}&rdquo;. Try clearing the search or pick a different cluster.
          </p>
        )}

        {/* Alphabet skip-nav */}
        {filtered.length > 0 && (
          <nav className="mt-8 flex flex-wrap gap-1.5" aria-label="Jump to letter">
            {letters.map((l) => (
              <a
                key={l}
                href={`#letter-${l}`}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md border bg-card font-mono text-[11px] font-semibold text-foreground/85 transition hover:border-primary/60 hover:text-primary"
              >
                {l}
              </a>
            ))}
          </nav>
        )}
      </section>

      <section className="container-tight pb-20">
        {letters.map((letter) => (
          <div key={letter} id={`letter-${letter}`} className="mb-10 scroll-mt-24">
            <h2 className="mb-4 inline-flex h-9 w-9 items-center justify-center rounded-lg bg-secondary font-display text-lg font-bold tracking-tight">
              {letter}
            </h2>
            <ul className="grid gap-3 md:grid-cols-2">
              {grouped[letter].map((e) => {
                const accent = CLUSTER_ACCENT[e.cluster]
                const clusterMeta = GLOSSARY_CLUSTERS.find((c) => c.id === e.cluster)!
                return (
                  <li key={e.slug}>
                    <Link
                      href={`/glossary/${e.slug}`}
                      className="group flex h-full flex-col rounded-2xl border bg-card p-5 transition hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md md:p-6"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] ${accent.chip}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${accent.dot}`} />
                          {clusterMeta.label}
                        </span>
                        <ArrowRight className="h-4 w-4 text-muted-foreground transition group-hover:translate-x-1 group-hover:text-primary" />
                      </div>
                      <h3 className="mt-3 font-display text-lg font-semibold tracking-tight md:text-xl">
                        {e.term}
                      </h3>
                      <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-foreground/75">
                        {e.shortAnswer}
                      </p>
                      {e.aliases && e.aliases.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {e.aliases.slice(0, 3).map((a) => (
                            <span key={a} className="rounded-full bg-secondary/60 px-2 py-0.5 text-[10px] text-foreground/65">
                              {a}
                            </span>
                          ))}
                        </div>
                      )}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </section>
    </>
  )
}
